import classNames from 'classnames';
import { Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import type { VideoJsPlayer } from 'video.js';
import './ShapesDrawingTools.less';
/* eslint-disable react/jsx-no-useless-fragment */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext
} from 'react';
import { FabricContext } from '@context/FabricContext';
import { colorPickerColorScheme } from '@helpers/ColorPickerColorScheme';
import { shapesScheme } from '@helpers/ShapesScheme';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useAuth } from '@hooks/useAuth';
import { useResponsive } from '@hooks/useResponsive';
import { LgWidth, MdWidth } from '@components/Responsive';
import { toggleSingInExternalReviewerModal } from '@redux/actions/modalAction';
import useCanvasHistory from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useCanvasHistory';
import {
  withKeysCanvasHistoryChange,
  withKeysCanvasHistoryCopy
} from '@helpers/withKeysCanvasHistoryChange';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import {
  isAnnotationDrawTool,
  PIN_DRAW_TOOL,
  CURSOR_DRAW_TOOL,
  DEFAULT_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';
import {
  UndoIcon,
  ShapesDrawingToolsArrowIcon,
  ShapesDrawingToolsCommentIcon,
  ShapesDrawingToolsCursorIcon,
  ShapesDrawingToolsDndIcon,
  ShapesDrawingToolsDrawIcon,
  ShapesDrawingToolsForwardIcon,
  ShapesDrawingToolsShowCommentIcon,
  ShapesDrawingToolsTextHighlightIcon
} from '@icons';

type ShapesDrawingToolsProps = {
  assetType?:
    | 'audio'
    | 'video'
    | 'document'
    | 'raster_image'
    | 'camera_raw_image'
    | 'vector_image'
    | 'website'
    | 'presentation'
    | 'spreadsheet';
  player?: VideoJsPlayer | null;
  className?: string;
};

function ShapesDrawingTools({
  assetType: assetTypeProp,
  player,
  className
}: ShapesDrawingToolsProps = {}) {
  const drawingToolsContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { isMdWidth } = useResponsive();
  const { tourData, goToStep } = useOnboardingProcess();
  const {
    shapesDrawingTools,
    isHideComments,
    selectedCommentId,
    selectedVersion,
    canvasMode,
    activePageHasText,
    commentText,
    documentHighlight,
    isCommentFieldFocused,
    isMobileDrawingToolbarOpen,
    setDrawToolsColor,
    setDrawToolsShape,
    selectComment,
    setDocumentHighlight,
    setCanvasMode,
    setHideComments,
    dismissAnnotation,
    setIsMobileDrawingToolbarOpen
  } = useAssetViewerState();
  const { getObjects, isMouseDownRef } = useContext(FabricContext);
  const assetTypeFromRedux = selectedVersion?.metadata.type;
  const assetType = assetTypeProp ?? assetTypeFromRedux;
  const tooltipTrigger = isMdWidth ? '' : ['hover'];
  const isAnnouncementMediaViewerTour =
    tourData?.type === 'announcement-media-viewer' && goToStep === 0;
  const isAnnouncementMediaViewerMobileTour =
    tourData?.type === 'announcement-media-viewer-mobile' && goToStep === 2;
  const isOpenTooltip = isAnnouncementMediaViewerTour ? true : undefined;
  const tourTooltipKey =
    tourData?.type === 'announcement-media-viewer' ? goToStep : 'default';

  const isAnnotationDrawing = isAnnotationDrawTool(shapesDrawingTools.shape);

  const { history, mods, redoHistory, undoHistory } = useCanvasHistory({
    assetType
  });
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const shouldHideCommentsButton =
    assetType === 'video' || assetType === 'audio' || assetType === 'website';
  const [shape, setShape] = useState<string>('rectangle');
  const [isColorPickerVisible, setIsColorPickerVisible] =
    useState<boolean>(false);
  const [isShapePickerVisible, setIsShapePickerVisible] =
    useState<boolean>(false);

  const withKeysHistoryChange = useCallback(
    (e: KeyboardEvent) => {
      if (!isAnnotationDrawing) return;
      withKeysCanvasHistoryChange({
        e,
        undo: undoHistory,
        redo: redoHistory,
        historyLength: history.length,
        mods
      });
    },
    [history.length, mods, redoHistory, undoHistory, isAnnotationDrawing]
  );

  const onShapeClick = useCallback(
    (shape: string) => {
      if (isMdWidth && user) {
        setIsMobileDrawingToolbarOpen(true);
      }

      const isViewingWebsiteComment =
        assetType === 'website' &&
        canvasMode === 'view' &&
        !!selectedCommentId &&
        selectedCommentId !== 'new' &&
        selectedCommentId !== 'reply';
      if (
        shape === CURSOR_DRAW_TOOL ||
        (shape === DEFAULT_DRAW_TOOL && !isMdWidth && !isViewingWebsiteComment)
      ) {
        if (selectedCommentId === 'new') {
          const hasDrawing = getObjects().length > 0;
          const hasText =
            !!commentText?.trim() || !!documentHighlight?.text?.trim();
          if (!hasDrawing && !hasText) {
            dismissAnnotation();
          }
        }
        setDrawToolsShape(shape);
        return;
      }
      if (!user) {
        dispatch(
          toggleSingInExternalReviewerModal({
            visible: true
          })
        );
        return;
      }
      if (canvasMode === 'view') {
        if (selectedCommentId !== 'reply') {
          selectComment('new');
          setDocumentHighlight(null);
        }
        setCanvasMode('edit');
      }
      if (player) player.pause();
      setDrawToolsShape(shape);
    },
    [
      canvasMode,
      dispatch,
      player,
      assetType,
      selectedCommentId,
      user,
      commentText,
      documentHighlight,
      getObjects,
      setDrawToolsShape,
      selectComment,
      setCanvasMode,
      setDocumentHighlight,
      isMdWidth,
      setIsMobileDrawingToolbarOpen
    ]
  );

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const modalElement = document.querySelector('.ant-modal-root');
      const fieldElement = document.querySelector(
        '.filter-sort-panel-search-input'
      );
      const transcriptSearchInput = document.getElementById(
        'TRANSCRIPT_SEARCH_INPUT'
      );
      withKeysHistoryChange(e);

      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (isTypingTarget) return;

      if (
        modalElement ||
        fieldElement ||
        transcriptSearchInput ||
        isCommentFieldFocused
      )
        return;
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === 'h') {
        e.preventDefault();
        setHideComments(!isHideComments);
        return;
      }
      if (key === 'v') {
        onShapeClick(CURSOR_DRAW_TOOL);
        return;
      }
      if (key === 'd') {
        onShapeClick('brush');
        return;
      }
      if (key === 'a') {
        onShapeClick('arrow');
        return;
      }
      if (key === 'h' && isDocument) {
        onShapeClick('documentHighlight');
        return;
      }
      if (key === 'c') {
        onShapeClick(PIN_DRAW_TOOL);
      }
    };
    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [
    withKeysHistoryChange,
    onShapeClick,
    isCommentFieldFocused,
    isDocument,
    assetType,
    isHideComments,
    setHideComments
  ]);

  const undoRedoTooltips = useMemo(() => {
    return withKeysCanvasHistoryCopy();
  }, []);

  const [dragging, setDragging] = useState(false);
  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains('dragger')) return;
      const toolsContainer = drawingToolsContainerRef.current;
      if (!toolsContainer) return;
      setDragging(true);
      toolsContainer.classList.add('shapes_drawing_tools_container--dragging');
      const rect = toolsContainer.getBoundingClientRect();
      toolsContainer.style.position = 'fixed';
      toolsContainer.style.top = `${rect.y - e.pageY}px`;
      toolsContainer.style.right = 'unset';
      toolsContainer.style.left = `${rect.x - e.pageX}px`;
      toolsContainer.style.transform = `translate(${e.pageX}px, ${e.pageY}px)`;
      toolsContainer.style.zIndex = '1000';

      const onMouseMove = (e: MouseEvent) => {
        const skip = e.pageX === 0 && e.pageY === 0;
        if (skip) return;
        requestAnimationFrame(() => {
          toolsContainer.style.transform = `translate(${e.pageX}px, ${e.pageY}px)`;
        });
      };
      document.addEventListener('mousemove', onMouseMove);

      const onMouseUp = () => {
        isMouseDownRef.current = false;
        setDragging(false);
        drawingToolsContainerRef.current?.classList.remove(
          'shapes_drawing_tools_container--dragging'
        );
        document.removeEventListener('mousemove', onMouseMove);
      };
      document.addEventListener('mouseup', onMouseUp, { once: true });
    },
    []
  );

  const onMobileToolbarContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isMdWidth || isMobileDrawingToolbarOpen) return;
      const target = e.target as HTMLElement;
      if (target.closest('.shapes_drawing_tools-item, .shape_picker')) return;
      if (target.closest('.color_picker_overlay, .shape_picker_overlay')) {
        return;
      }
      setIsMobileDrawingToolbarOpen(true);
    },
    [isMdWidth, isMobileDrawingToolbarOpen, setIsMobileDrawingToolbarOpen]
  );
  return (
    <>
      {!shouldHideCommentsButton && (
        <MdWidth>
          <Tooltip
            title={
              <div
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Hide comments
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="left"
            overlayClassName="toggle_favorite_overlay b-show-arrow"
            open={isAnnouncementMediaViewerMobileTour}
          >
            <span
              className={classNames(
                isHideComments && 'selected',
                isMobileDrawingToolbarOpen &&
                  'shapes_drawing_tools_container_open',
                'shapes_drawing_tools-mobile-hide-comments',
                className
              )}
              onClick={() => {
                setHideComments(!isHideComments);
              }}
            >
              <ShapesDrawingToolsShowCommentIcon />
            </span>
          </Tooltip>
        </MdWidth>
      )}
      <div
        ref={drawingToolsContainerRef}
        onMouseDown={onMouseDown}
        onClick={onMobileToolbarContainerClick}
        className={classNames(
          isMobileDrawingToolbarOpen && 'shapes_drawing_tools_container_open',
          'shapes_drawing_tools_container',
          'tour-review-asset-tool-bar',
          'tour-announcement-media-viewer-tool-bar',
          isAnnouncementMediaViewerTour &&
            'tour-announcement-media-viewer-tool-bar-active',
          className
        )}
      >
        <div className="wrapper" key={tourTooltipKey}>
          <Tooltip
            title={
              <div
                onClick={() => {
                  onShapeClick(CURSOR_DRAW_TOOL);
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Select
                <span className="media-viewer-tooltip-label">V</span>
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
            trigger={tooltipTrigger}
            open={isOpenTooltip}
          >
            <span
              className={classNames(
                shapesDrawingTools?.shape === CURSOR_DRAW_TOOL && 'selected',
                'anticon anticon-font-size shapes_drawing_tools-item'
              )}
              onClick={() => {
                onShapeClick(CURSOR_DRAW_TOOL);
              }}
            >
              <ShapesDrawingToolsCursorIcon />
            </span>
          </Tooltip>

          <Tooltip
            title={
              <div
                onClick={() => {
                  onShapeClick(PIN_DRAW_TOOL);
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Comment
                <span className="media-viewer-tooltip-label">C</span>
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
            trigger={tooltipTrigger}
            open={isOpenTooltip}
          >
            <span
              className={classNames(
                shapesDrawingTools?.shape === PIN_DRAW_TOOL && 'selected',
                'anticon anticon-font-size shapes_drawing_tools-item b-pin-item'
              )}
              onClick={() => {
                onShapeClick(PIN_DRAW_TOOL);
              }}
            >
              <ShapesDrawingToolsCommentIcon />
            </span>
          </Tooltip>
          <span className="divider b-full" />
          <Tooltip
            title={
              <div
                onClick={() => {
                  setIsColorPickerVisible(true);
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Switch color
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
            trigger={tooltipTrigger}
          >
            <Tooltip
              placement={isMdWidth ? 'top' : 'right'}
              overlayClassName="color_picker_overlay"
              trigger={['click']}
              open={isColorPickerVisible}
              onOpenChange={(v) => setIsColorPickerVisible(v)}
              title={
                <div className="color_picker_overlay_container">
                  {colorPickerColorScheme.map((el) => (
                    <div
                      key={el}
                      className="color_picker_overlay_container__item"
                      style={{ background: el }}
                      onClick={() => {
                        setIsColorPickerVisible(false);
                        setDrawToolsColor(el);
                        if (
                          shapesDrawingTools.shape === PIN_DRAW_TOOL ||
                          shapesDrawingTools.shape === CURSOR_DRAW_TOOL
                        ) {
                          onShapeClick('brush');
                        }
                      }}
                    />
                  ))}
                </div>
              }
            >
              <div className="anticon shapes_drawing_tools-item">
                <div
                  className="color_picker"
                  style={{ background: shapesDrawingTools?.color }}
                />
              </div>
            </Tooltip>
          </Tooltip>
          <span className="divider" />
          {isDocument && activePageHasText && (
            <Tooltip
              title={
                <div
                  onClick={() => {
                    onShapeClick('documentHighlight');
                  }}
                  className="media-viewer-tooltip-inner"
                  style={{ padding: 8 }}
                >
                  Text highlight
                  <span className="media-viewer-tooltip-label">H</span>
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="right"
              overlayClassName="toggle_favorite_overlay"
              trigger={tooltipTrigger}
              open={isOpenTooltip}
            >
              <span
                className={classNames(
                  shapesDrawingTools?.shape === 'documentHighlight' &&
                    'selected',
                  'anticon anticon-font-size shapes_drawing_tools-item tour-text-highlight-bar'
                )}
                onClick={() => onShapeClick('documentHighlight')}
              >
                <ShapesDrawingToolsTextHighlightIcon />
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={
              <div
                onClick={() => {
                  onShapeClick('arrow');
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Arrow
                <span className="media-viewer-tooltip-label">A</span>
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
            trigger={tooltipTrigger}
            open={isOpenTooltip}
          >
            <span
              className={classNames(
                shapesDrawingTools?.shape === 'arrow' && 'selected',
                'anticon anticon-font-size shapes_drawing_tools-item'
              )}
              onClick={() => {
                onShapeClick('arrow');
              }}
            >
              <ShapesDrawingToolsArrowIcon style={{ fontSize: 40 }} />
            </span>
          </Tooltip>
          <Tooltip
            title={
              <div
                onClick={() => {
                  setIsShapePickerVisible(true);
                  onShapeClick(shape);
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Shape tools
              </div>
            }
            overlayInnerStyle={{
              padding: 0,
              display: isShapePickerVisible ? 'none' : 'block'
            }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
            trigger={tooltipTrigger}
            open={isOpenTooltip}
          >
            <div
              onClick={() => {
                onShapeClick(shape);
                if (shapesDrawingTools?.shape === shape) {
                  setIsShapePickerVisible(true);
                }
              }}
              className={classNames(
                'shape_picker',
                'shapes_drawing_tools-item',
                shapesDrawingTools?.shape === shape && 'selected'
              )}
            >
              <div className={classNames(`shape_picker__${shape}`)} />
              <Tooltip
                placement={isMdWidth ? 'top' : 'right'}
                overlayClassName="shape_picker_overlay"
                trigger={['click']}
                open={isShapePickerVisible}
                onOpenChange={(v) => setIsShapePickerVisible(v)}
                title={
                  <div className="shape_picker_overlay_container">
                    {shapesScheme.map((el) => (
                      <Tooltip
                        title={el}
                        key={el}
                        placement="right"
                        overlayInnerStyle={{
                          padding: '9px 8px',
                          textTransform: 'capitalize'
                        }}
                        overlayClassName="toggle_favorite_overlay"
                      >
                        <div
                          key={el}
                          className={`shape_picker_overlay_container__item ${
                            shape === el &&
                            'shape_picker_overlay_container__selected_item'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsShapePickerVisible(false);
                            onShapeClick(el);
                            setShape(el);
                          }}
                        >
                          <div className={`shape shape_${el}`} />
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                }
              />
            </div>
          </Tooltip>
          <Tooltip
            title={
              <div
                onClick={() => {
                  onShapeClick('brush');
                }}
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                {isAnnouncementMediaViewerMobileTour && 'Draw'}
                {!isAnnouncementMediaViewerMobileTour && (
                  <>
                    Draw
                    <span className="media-viewer-tooltip-label">D</span>
                  </>
                )}
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement={isAnnouncementMediaViewerMobileTour ? 'left' : 'right'}
            overlayClassName={classNames('toggle_favorite_overlay', {
              'b-show-arrow': isAnnouncementMediaViewerMobileTour
            })}
            trigger={tooltipTrigger}
            open={isOpenTooltip || isAnnouncementMediaViewerMobileTour}
          >
            <span
              className={classNames(
                'anticon anticon-font-size',
                'shape_picker',
                'shapes_drawing_tools-item',
                shapesDrawingTools?.shape === 'brush' && 'selected'
              )}
              onClick={() => {
                onShapeClick('brush');
              }}
            >
              <ShapesDrawingToolsDrawIcon />
            </span>
          </Tooltip>
          {!shouldHideCommentsButton && (
            <LgWidth>
              <span className="divider b-full" />
              <Tooltip
                title={
                  <div
                    className="media-viewer-tooltip-inner"
                    style={{ padding: 8 }}
                  >
                    {isHideComments ? 'Show' : 'Hide'} comments
                    <span className="media-viewer-tooltip-label">
                      {undoRedoTooltips.toggleShowComments}
                    </span>
                  </div>
                }
                overlayInnerStyle={{ padding: 0 }}
                placement="right"
                overlayClassName="toggle_favorite_overlay"
                trigger={tooltipTrigger}
                open={isOpenTooltip}
              >
                <span
                  className={classNames(
                    isHideComments && 'selected',
                    'anticon anticon-font-size shapes_drawing_tools-item'
                  )}
                  onClick={() => {
                    setHideComments(!isHideComments);
                  }}
                >
                  <ShapesDrawingToolsShowCommentIcon />
                </span>
              </Tooltip>
            </LgWidth>
          )}
        </div>
        <LgWidth>
          <div className="wrapper">
            <Tooltip
              title={
                <div
                  style={{ padding: 8 }}
                  onClick={undoHistory}
                  className="media-viewer-tooltip-inner"
                >
                  Undo
                  <span className="media-viewer-tooltip-label">
                    {undoRedoTooltips.undo}
                  </span>
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="right"
              overlayClassName="toggle_favorite_overlay"
            >
              <span
                className={classNames(
                  'anticon anticon-font-size',
                  mods < history.length && 'arrow_active',
                  'arrow',
                  'shapes_drawing_tools-item'
                )}
                onClick={undoHistory}
              >
                <UndoIcon />
              </span>
            </Tooltip>
            <Tooltip
              title={
                <div
                  style={{ padding: 8 }}
                  onClick={redoHistory}
                  className="media-viewer-tooltip-inner"
                >
                  Redo
                  <span className="media-viewer-tooltip-label">
                    {undoRedoTooltips.redo}
                  </span>
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="right"
              overlayClassName="toggle_favorite_overlay"
              trigger={tooltipTrigger}
            >
              <span
                className={classNames(
                  'anticon anticon-font-size',
                  mods > 0 && 'arrow_active',
                  'arrow',
                  'shapes_drawing_tools-item'
                )}
                onClick={redoHistory}
              >
                <ShapesDrawingToolsForwardIcon style={{ fontSize: 40 }} />
              </span>
            </Tooltip>
          </div>
          <Tooltip
            title={dragging ? null : 'Move toolbar'}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
            trigger={tooltipTrigger}
          >
            <div className="dragger">
              <ShapesDrawingToolsDndIcon />
            </div>
          </Tooltip>
        </LgWidth>
      </div>
    </>
  );
}
export default ShapesDrawingTools;
